import { dataSourceWholeItems } from '@/resources/data-source';
import { DataSourceFormat } from '@/resources/enum';
import { ElectronApi } from '@/services/electron.api';
import { ShapeType } from '@/services/shape';
import { useDataSourceStore } from '@/store/use-data-source-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useEditorStore } from '@/store/use-editor-store';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  kPaToMmHg,
  mmHgToKpa
} from '@/utils/ast/utils/data-source.utils';
import {
  canUse,
  canUseDataSource,
  disabledDataSourceFormat
} from '@/utils/can-use.utils';
import { EventType, emitter } from '@/utils/emitter.utils';
import { getIntValue, getPrecisionValue } from '@/utils/number';
import { round } from 'lodash-es';
import { ref } from 'vue';

const _configData = ref<Record<string, number | string>>({});
const _expand = ref(false);

export const useDataSource = () => {
  const watchfaceStroe = useWatchfaceStore();
  const dataSourceStore = useDataSourceStore();
  const editorStore = useEditorStore();

  /**
   * 查找数据项
   * @param sourceKey 数据源key
   * @returns
   */
  const findOne = (sourceKey: string,type?: string) => {
    const res = dataSourceWholeItems.find((item) => item.key == sourceKey);
    // 时间类的数据源需要判断时间格式
    if (res && ['time','dualtime'].includes(sourceKey)) {
      let isProfessional = !(ElectronApi.isNormal() || editorStore.theme?.type === 'AOD' || type === 'AOD');
      res.returnValue = isProfessional?'[00:00:00, 24:00:00)':'[00:00, 24:00)';
      res.formats = isProfessional ?
        [DataSourceFormat['Format_8:00:00'],DataSourceFormat['Format_08:00:00']] : 
        [DataSourceFormat['Format_8:00'],DataSourceFormat['Format_08:00']];
      if (sourceKey === 'dualtime') {
        res.comment =  isProfessional ? '目标时区的时分秒' : '目标时区的时分';
      }
      if (sourceKey === 'time') {
        res.comment = isProfessional ? '时,分和秒（跟随系统24H/12H设置）' : '时和分（跟随系统24H/12H设置）'
      }
    }
    return res;
  };

  /**
   * 是否为血压多单位数据源
   * @param key
   * @returns
   */
  const isHealthBloodPressure = (key: string) => {
    return (
      key.includes('healthBlood') &&
      (key.includes('PressureKpa') || key.includes('PressureMmhg'))
    );
  };
  /**
   * 是否为气温多单位数据源
   * @param key
   * @returns
   */
  const isWeatherTemperature = (key: string) => {
    return (
      !['weatherTemperatureUnit', 'weatherCurrentTemperatureFeel'].includes(
        key
      ) &&
      key.includes('weather') &&
      key.includes('Temperature')
    );
  };

  /**
   * 为数据源key绑定多个值
   * @param assignments
   */
  const setValues = (assignments: Record<string, number | string>) => {
    const _configData2 = {
      ..._configData.value,
      ...assignments
    };
    const keys = Object.keys(assignments);

    const _setProgress = (
      countKey: string,
      targetKey: string,
      progressKey: string
    ) => {
      const healthStepTarget = Number(
        _configData2[targetKey] ?? findOne(targetKey)?.defaultValue ?? 0
      );

      if (
        (keys.includes(countKey) || keys.includes(targetKey)) &&
        !keys.includes(progressKey)
      ) {
        const healthStepCount = Number(_configData2[countKey] ?? 0);
        const progress = Number.parseInt(
          round((healthStepCount / healthStepTarget) * 100, 4) + ''
        );
        _configData2[progressKey] = progress
          ? Math.max(Math.min(100, progress), 0)
          : 0;
      }
      // 步数完成度
      if (keys.includes(progressKey)) {
        const healthStepProgress = Number(_configData2[progressKey] ?? 0);

        const healthStepCount = Math.floor(
          (healthStepTarget * healthStepProgress) / 100
        );
        _configData2[countKey] = healthStepCount
          ? Math.max(
              Math.min(findOne(targetKey)?.maxValue ?? 0, healthStepCount),
              0
            )
          : 0;
      }
    };

    // 步数
    _setProgress('healthStepCount', 'healthStepTarget', 'healthStepProgress');
    // 卡路里
    _setProgress(
      'healthCalorieValue',
      'healthCalorieTarget',
      'healthCalorieProgress'
    );
    // 站立
    _setProgress(
      'healthStandCount',
      'healthStandTarget',
      'healthStandProgress'
    );
    // 中高强调锻炼
    _setProgress(
      'healthExerciseDuration',
      'healthExerciseTarget',
      'healthExerciseProgress'
    );
    // 睡眠时长
    _setProgress(
      'healthSleepDuration',
      'healthSleepTarget',
      'healthSleepTargetProgress'
    );

    keys.forEach((key) => {
      // 血压数据源联动
      if (isHealthBloodPressure(key)) {
        const isMmHg = key.includes('Mmhg');
        const val = _configData2[key] as number;
        const anotherKey = isMmHg
          ? key.replace('Mmhg', 'Kpa')
          : key.replace('Kpa', 'Mmhg');
        const anotherVal = isMmHg ? mmHgToKpa(val) : kPaToMmHg(val);
        _configData2[anotherKey] = anotherVal;
      }
      // 天气不同单位联动
      if (isWeatherTemperature(key)) {
        const isFahrenheit = key.includes('Fahrenheit');
        const val = _configData2[key] as number;
        const anotherKey = isFahrenheit
          ? key.replace('Fahrenheit', '')
          : key + 'Fahrenheit';
        const anotherVal = isFahrenheit
          ? fahrenheitToCelsius(val)
          : celsiusToFahrenheit(val);
        _configData2[anotherKey] = anotherVal;
      }
    });

    _configData.value = _configData2;
    emitter.emit(EventType['config:datachanged']);
  };

  /**
   * 重置为默认数据
   */
  const reset = (emiter = true) => {
    _configData.value = dataSourceStore.wholeValues(undefined);
    const appAlarm = Number(_configData.value['appAlarm']);
    if (_configData.value['appAlarmHour'] == undefined) {
      _configData.value['appAlarmHour'] = getIntValue(appAlarm);
    }
    if (_configData.value['appAlarmMinute'] == undefined) {
      _configData.value['appAlarmMinute'] = getPrecisionValue(appAlarm) * 60;
    }

    if (emiter) emitter.emit(EventType['config:datachanged']);
  };

  /**
   * 获取最大和最小数值（用于图片和进度条旋转使用）
   * @param item
   */
  const getValueStartEnd = (sourceKey: string) => {
    const dataSource = findOne(sourceKey);
    const dataSource2 = findOne(dataSource?.children?.[0] ?? '');

    let valueStart: number | string =
      dataSource2?.minValue ?? dataSource?.minValue ?? 0;
    let valueEnd: number | string =
      dataSource2?.maxValue ?? dataSource?.maxValue ?? 0;

    const deviceType = watchfaceStroe.deviceType;

    const setSourceValueIfNeed = () => {
      let valueStart2: string | number | undefined = undefined;
      let valueEnd2: string | undefined = undefined;

      switch (dataSource?.key) {
        case 'healthStepCount':
          valueStart2 = 0;
          valueEnd2 = 'healthStepTarget';
          break;
        case 'healthHeartRate':
          valueStart2 = 'healthHeartRateMin';
          valueEnd2 = 'healthHeartRateMax';
          break;
        case 'weatherCurrentTemperature':
          valueStart2 = 'weatherTodayTemperatureMin';
          valueEnd2 = 'weatherTodayTemperatureMax';
          break;
        case 'weatherCurrentTempature_temp':
          valueStart2 = 'weatherTodayTemperatureMin_temp';
          valueEnd2 = 'weatherTodayTemperatureMax_temp';
          break;
        case 'weatherCurrentTemperatureFahrenheit':
          valueStart2 = 'weatherTodayTemperatureMinFahrenheit';
          valueEnd2 = 'weatherTodayTemperatureMaxFahrenheit';
          break;

        default:
          break;
      }
      if (valueStart2 != undefined) {
        if (
          typeof valueStart2 === 'number' ||
          canUseDataSource(valueStart2, deviceType)
        ) {
          valueStart = valueStart2;
        }
      }
      if (valueEnd2 != undefined) {
        if (
          typeof valueEnd2 === 'number' ||
          canUseDataSource(valueEnd2, deviceType)
        ) {
          valueEnd = valueEnd2;
        }
      }
    };
    if (canUse('data-source-bind-max-min-value-support', deviceType).valid) {
      setSourceValueIfNeed();
    }

    return {
      valueStart,
      valueEnd
    };
  };

  const getFormatOptions = (source: string, shapeType?: ShapeType) => {
    const sourceDataItem = findOne(source);
    const deviceType = watchfaceStroe.deviceType;

    const options: {
      label: string;
      value: DataSourceFormat;
      disabled?: boolean;
    }[] = [];
    const unit = sourceDataItem?.unit;
    const sourceFormat = sourceDataItem?.formatsFn
      ? sourceDataItem?.formatsFn(deviceType, shapeType)
      : sourceDataItem?.formats;
    sourceFormat?.forEach((item, index) => {
      let label = '';
      switch (item) {
        case DataSourceFormat.Format_8:
          label = `8`;
          break;
        case DataSourceFormat.Format_08:
          label = `08`;
          break;
        case DataSourceFormat.Format_8时:
          label = `8${unit}`;
          break;
        case DataSourceFormat.Format_08时:
          label = `08${unit}`;
          break;
        case DataSourceFormat.Format_SPF8:
          label = `${unit}8`;
          break;
        case DataSourceFormat['Format_8:00']:
          label = `8:00`;
          break;
        case DataSourceFormat['Format_08:00']:
          label = `08:00`;
          break;
        case DataSourceFormat['Format_8:00:00']:
          label = `8:00:00`;
          break;
        case DataSourceFormat['Format_08:00:00']:
          label = `08:00:00`;
          break;
        case DataSourceFormat['Format_8.0']:
          label = `8.0`;
          break;
        case DataSourceFormat['Format_8.0时']:
          label = `8.0${unit}`;
          break;
        case DataSourceFormat['Format_SPF8.0']:
          label = `${unit}8.0`;
          break;
        case DataSourceFormat['Format_8/16']:
          label = `8/16`;
          break;
        case DataSourceFormat['Format_08/16']:
          label = `08/16`;
          break;
        case DataSourceFormat['Format_text_8/16']:
          label = `8/16 日不补零`;
          break;
        case DataSourceFormat.Format_8月16日:
          label = `8月16日`;
          break;
        case DataSourceFormat.Format_08月16日:
          label = `08月16日`;
          break;
        case DataSourceFormat.Format_text_8月16日:
          label = `8月16日 日不补零`;
          break;
        case DataSourceFormat.Format_甲子:
          label = `甲子`;
          break;
        case DataSourceFormat.Format_甲子年:
          label = `甲子年`;
          break;
      }

      options.push({
        label: `格式${index + 1}（${label}）`,
        value: item,
        disabled: disabledDataSourceFormat(sourceDataItem?.key ?? '', item)
      });
    });

    return options;
  };

  /**
   * 获取相关联动数据源
   * @param key
   * @returns
   */
  return {
    expand: _expand,
    findOne,
    setValues,
    configData: _configData,
    reset,
    getValueStartEnd,
    getFormatOptions,
    isHealthBloodPressure,
    isWeatherTemperature
  };
};

export const toRelatedSourceKeys = (key: string) => {
  const keys: string[] = [];
  if (key.endsWith('Low')) {
    key = key.replace('Low', '');
  }
  if (key.endsWith('High')) {
    key = key.replace('High', '');
  }
  if (key.startsWith('dateYearDigit')) {
    key = 'dateYear';
  }
  if (key.startsWith('dateWeekString')) {
    key = 'dateWeek';
  }
  // 双时区
  if (key.startsWith('dualtimeStringCity')) {
    key = 'dualtimeCity';
  }
  if (key.startsWith('dateMonthString')) {
    key = 'dateMonth';
  }
  if (key.startsWith('healthStepCount')) {
    key = 'healthStepCount';
  }
  if (key.startsWith('healthCalorieValue')) {
    key = 'healthCalorieValue';
  }
  if (key.startsWith('healthExerciseDuration')) {
    key = 'healthExerciseDuration';
  }
  if (key.startsWith('healthStandCount')) {
    key = 'healthStandCount';
  }
  if (key.startsWith('miscIsPM')) {
    key = 'timeHour';
  }
  if (key.startsWith('dualtimeIsPM')) {
    key = 'dualtimeHour';
  }
  if (key.startsWith('miscTimeSection')) {
    key = 'time';
    keys.push(...['weatherCurrentSunRise', 'weatherCurrentSunSet']);
  }
  if (key.startsWith('time')) {
    // key = 'time';
    keys.push('time', 'timeHour', 'timeMinute', 'timeSecond');
  }
  if (key.startsWith('dualtime')) {
    // key = 'time';
    keys.push('dualtimeHour', 'dualtimeMinute', 'dualtimeSecond');
  }
  if (key.startsWith('appAlarm')) {
    key = 'appAlarm';
  }
  if (key.startsWith('miscdate') || key == 'dateWeek') {
    key = 'dateMonth';
    keys.push(...['dateMonth', 'dateDay', 'dateYear']);
  }
  if (
    key.startsWith('dateLunarStringYear') ||
    key == 'dateSexagenaryCycle' ||
    key.startsWith('dateLunar')
  ) {
    key = 'dateYear';
    keys.push(...['dateMonth', 'dateDay']);
  }
  if (key.startsWith('weatherCurrentSunRise')) {
    key = 'weatherCurrentSunRise';
  }
  if (key.startsWith('weatherCurrentSunSet')) {
    key = 'weatherCurrentSunSet';
  }
  if (key.startsWith('timeChineseTime')) {
    key = 'timeHour24';
  }
  // 使用血压时，增加血压单位
  if (key.startsWith('healthBlood') && key.includes('Pressure')) {
    keys.push(...['healthBloodPressureUnit']);
  }
  // 使用温度时，增加温度单位
  if (
    key.startsWith('weather') &&
    key.includes('Temperature') &&
    canUse('temperature-fahrenheit').valid
  ) {
    keys.push(...['weatherTemperatureUnit']);
  }
  // 卡路里目标
  if (key.startsWith('healthCalorieProgress')) {
    keys.push(...['healthCalorieTarget']);
  }
  // 步数目标
  if (key.startsWith('healthStepProgress')) {
    keys.push(...['healthStepTarget']);
  }
  // 站立目标
  if (key.startsWith('healthStandProgress')) {
    keys.push(...['healthStandTarget']);
  }
  // 站立目标
  if (key.startsWith('healthExerciseProgress')) {
    keys.push(...['healthExerciseTarget']);
  }

  // 心率区间用心率去控制
  if (key === 'healthHeartRateZone') {
    key = 'healthHeartRate';
    // 心率区间和心率最大值有关，需要增加上
    keys.push(...['healthHeartRateMax']);
  }

  // 禁飞时间单位
  if (key == 'appSportNoFlyTimeUnit') {
    key = 'appSportNoFlyTime';
  }
  // 睡眠间隔时间
  if (key == 'appSportSitTimeUnit') {
    key = 'appSportSitTime';
  }

  // 指南针方向
  if (key == 'systemSensorCompassDirection') {
    key = 'systemSensorCompass';
  }
  if (key.startsWith('dateMoon')) {
    key = 'dateYear';
    keys.push(...['dateMonth', 'dateDay']);
  }
  keys.push(key);

  return keys;
};
